/**
 * ActivityLogBox - Caja «Log de actividad (quién hizo qué)» en fichas de entidad.
 * Usa GET /api/crm/activity-log. Muestra actor (enlace a ficha admin si admin), acción y fecha.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { crmService } from '@/services/crmService';
import { formatDateTime } from '@/utils/formatters';
import { getActivityActionLabel } from '@/utils/activityLogLabels';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { UserAvatar } from '@/components/CRM/UserAvatar';
import type { AuditLogEntry } from '@/types/audit';

export interface ActivityLogBoxProps {
  /** Tipo de entidad: contact, appointment, task, note, opportunity, contract, call */
  entityType: string;
  entityId: string;
  /** Máximo de ítems (default 50) */
  limit?: number;
  /** Título del card */
  title?: string;
}

const DEFAULT_TITLE = 'Log de actividad (quién hizo qué)';

export function ActivityLogBox({
  entityType,
  entityId,
  limit = 50,
  title = DEFAULT_TITLE,
}: ActivityLogBoxProps) {
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId || entityId === 'new') {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    crmService
      .getActivityLog({ entity_type: entityType, entity_id: entityId, limit })
      .then((res) => {
        if (!cancelled) setItems(res.items ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          if (err?.response?.status === 403) {
            setError('No tienes permisos para ver el log de actividad.');
          } else if (err?.response?.status === 404) {
            setItems([]);
          } else {
            setError(err?.message ?? 'Error al cargar el log de actividad.');
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, limit]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ChartBarIcon className="h-4 w-4" aria-hidden />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-6 text-center text-sm text-muted-foreground">Cargando…</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ChartBarIcon className="h-4 w-4" aria-hidden />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-amber-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ChartBarIcon className="h-4 w-4" aria-hidden />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No hay actividad registrada.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ChartBarIcon className="h-4 w-4" aria-hidden />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3" role="list">
          {items.map((entry) => {
            const actorLabel = entry.actor_name ?? entry.actor_email ?? 'Sistema';
            return (
            <li
              key={entry.id}
              className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-700 min-h-[44px]"
            >
              <UserAvatar
                userId={entry.actor_id}
                name={entry.actor_name ?? entry.actor_email}
                size="sm"
              />
              <span className="flex items-center gap-1 font-medium text-gray-900 min-w-0">
                {entry.actor_ficha_path ? (
                  <Link
                    to={entry.actor_ficha_path}
                    className="text-primary hover:underline focus-visible:outline focus-visible:ring-2 focus-visible:ring-ring rounded"
                  >
                    {actorLabel}
                  </Link>
                ) : (
                  actorLabel
                )}
              </span>
              <span className="text-gray-600">{getActivityActionLabel(entry.action)}</span>
              <span className="text-xs text-muted-foreground shrink-0">
                {entry.created_at ? formatDateTime(entry.created_at) : ''}
              </span>
            </li>
          );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
